import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env';
import { _isDev, _log, _warn } from '@shared/aux_helper_environment';
import { Observable } from 'rxjs/internal/Observable';
import { catchError, map } from 'rxjs/operators';
import { normalizeSamplingConfiguration } from './bedrock-sampling';

export interface AIRequest {
  prompt: string;
  maxTokens?: number;
  temperature?: number;
  model?: string;
  stream?: boolean;
}

export interface AIResponse {
  content: string;
  error?: string;
}

export interface BedrockModelConfiguration {
  temperature?: number;
  maxTokens?: number;
  /**
   * EXCLUYENTE con temperature: los modelos Claude 4+ rechazan el request entero si vienen los dos
   * ("`temperature` and `top_p` cannot both be specified for this model"). Elegí uno solo.
   *
   * Si igual llegan ambos, el servicio descarta topP y deja temperature (ver normalizeBedrockConfiguration).
   */
  topP?: number;
  topK?: number;
  stopSequences?: string[];
  presencePenalty?: number;
  frequencyPenalty?: number;
}

export interface BedrockRequest {
  modelId: string;
  prompt: string;
  configuration?: BedrockModelConfiguration;
  useStreaming?: boolean;
}

export interface BedrockResponse {
  success: boolean;
  statusCode: number;
  content?: string;
  errorMessage?: string;
  metadata?: {
    modelId: string;
    responseTimeMs: number;
    requestTimestamp: string;
    usedStreaming: boolean;
  };
}

export interface BedrockModel {
  modelId: string;
  provider: string;
  displayName: string;
  description?: string;
  isAvailable: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AiApiWrapperService {
  private readonly deepSeekApiUrl = environment.aiApiWrapperService.deepSeekApiUrl;
  private readonly claudeApiUrl = environment.aiApiWrapperService.claudeApiUrl;
  private readonly bedrockApiUrl = this.getBedrockApiUrl();

  constructor(private http: HttpClient) {}

  private getBedrockApiUrl(): string {
    if (_isDev()) {
      return 'https://lab.prismaretail.dev/api/datascience/bedrockproxy';
    }

    return environment.aiApiWrapperService.bedrockApiUrl || '/api/datascience/bedrockproxy';
  }

  /**
   * Aplica la regla de sampling de Claude 4+ (ver bedrock-sampling.ts) sumándole el logging.
   *
   * Se hace acá, que es el único punto por el que pasan todas las llamadas a Bedrock, así ningún
   * llamador puede volver a romperlo: el error se ve recién en runtime y contra el modelo real.
   */
  private normalizeBedrockConfiguration(request: BedrockRequest): BedrockRequest {
    const configuration = normalizeSamplingConfiguration(request?.configuration, ({ campo, valor }) =>
      _warn(`[Bedrock] temperature y topP son excluyentes en Claude 4+; se descarta ${campo}`, {
        modelId: request?.modelId,
        descartado: valor,
      })
    );

    return configuration === request?.configuration ? request : { ...request, configuration };
  }

  generateWithDeepSeek(request: AIRequest): Observable<AIResponse> {
    const headers = new HttpHeaders()
      .set('Authorization', `Bearer ${environment.aiApiWrapperService.deepSeekApiKey}`)
      .set('Content-Type', 'application/json');

    const payload = {
      model: request.model || 'deepseek-chat',
      messages: [{ role: 'user', content: request.prompt }],
      max_tokens: request.maxTokens || 1000,
      temperature: request.temperature || 0.7,
      stream: request.stream || false,
    };

    return this.http.post(this.deepSeekApiUrl, payload, { headers }).pipe(
      map((response: any) => ({
        content: response.choices[0].message.content,
      })),
      catchError(error => {
        throw {
          content: '',
          error: error.message || 'Error al comunicarse con DeepSeek API',
        };
      })
    );
  }

  generateWithClaude(request: AIRequest): Observable<AIResponse> {
    let headers = new HttpHeaders()
      .set('anthropic-version', '2023-06-01')
      .set('Content-Type', 'application/json')
      .set('anthropic-dangerous-direct-browser-access', 'true');

    // Verificar modo dev y puerto 4200
    // if (_isDev() && window.location.port === '4200') {
    headers = headers.set('x-api-key', environment.aiApiWrapperService.claudeApiKey);
    // }

    const payload = {
      model: request.model || 'claude-sonnet-4-20250514',
      messages: [{ role: 'user', content: request.prompt }],
      max_tokens: request.maxTokens || 1024,
    };

    const url = this.claudeApiUrl + '/v1/messages';

    return this.http.post(url, payload, { headers }).pipe(
      map((response: any) => ({
        content: response.content[0].text,
      })),
      catchError(error => {
        throw {
          content: '',
          error: error.message || 'Error al comunicarse con Claude API',
        };
      })
    );
  }

  // AWS Bedrock Methods

  /**
   * Invoca un modelo de AWS Bedrock con un prompt específico
   */
  generateWithBedrock(rawRequest: BedrockRequest): Observable<BedrockResponse> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');

    const url = `${this.bedrockApiUrl}/invoke`;
    const request = this.normalizeBedrockConfiguration(rawRequest);

    if (_isDev()) {
      _log('BedrockRequest:', request);
    }

    return this.http.post<BedrockResponse>(url, request, { headers }).pipe(
      map((response: BedrockResponse) => {
        if (_isDev()) {
          _log('BedrockResponse:', response);
        }
        return response;
      }),
      catchError(error => {
        const errorResponse: BedrockResponse = {
          success: false,
          statusCode: error.status || 500,
          errorMessage: error.error?.errorMessage || error.message || 'Error al comunicarse con AWS Bedrock',
          metadata: {
            modelId: request.modelId,
            responseTimeMs: 0,
            requestTimestamp: new Date().toISOString(),
            usedStreaming: request.useStreaming || false,
          },
        };

        if (_isDev()) {
          _log('BedrockError:', errorResponse);
        }

        throw errorResponse;
      })
    );
  }

  /**
   * Invoca un modelo de AWS Bedrock con streaming
   */
  generateWithBedrockStream(rawRequest: BedrockRequest): Observable<BedrockResponse> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');

    const url = `${this.bedrockApiUrl}/stream`;

    const request = this.normalizeBedrockConfiguration(rawRequest);
    const streamRequest = { ...request, useStreaming: true };

    if (_isDev()) {
      _log('BedrockStreamRequest:', streamRequest);
    }

    return this.http.post<BedrockResponse>(url, streamRequest, { headers }).pipe(
      map((response: BedrockResponse) => {
        if (_isDev()) {
          _log('BedrockStreamResponse:', response);
        }
        return response;
      }),
      catchError(error => {
        const errorResponse: BedrockResponse = {
          success: false,
          statusCode: error.status || 500,
          errorMessage: error.error?.errorMessage || error.message || 'Error al comunicarse con AWS Bedrock Stream',
          metadata: {
            modelId: request.modelId,
            responseTimeMs: 0,
            requestTimestamp: new Date().toISOString(),
            usedStreaming: true,
          },
        };

        if (_isDev()) {
          _log('BedrockStreamError:', errorResponse);
        }

        throw errorResponse;
      })
    );
  }

  /**
   * Obtiene la lista de modelos disponibles en AWS Bedrock
   */
  getBedrockModels(): Observable<BedrockModel[]> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');

    const url = `${this.bedrockApiUrl}/models`;

    return this.http.get<BedrockModel[]>(url, { headers }).pipe(
      map((models: BedrockModel[]) => {
        if (_isDev()) {
          _log('BedrockModels:', models);
        }
        return models;
      }),
      catchError(error => {
        if (_isDev()) {
          _log('BedrockModelsError:', error);
        }
        throw {
          error: error.message || 'Error al obtener los modelos de AWS Bedrock',
        };
      })
    );
  }

  /**
   * Valida si un modelo específico está disponible en AWS Bedrock
   */
  validateBedrockModel(modelId: string): Observable<boolean> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');

    const url = `${this.bedrockApiUrl}/models/${encodeURIComponent(modelId)}/available`;

    return this.http.get<boolean>(url, { headers }).pipe(
      map((isAvailable: boolean) => {
        if (_isDev()) {
          _log(`BedrockModelValidation for ${modelId}:`, isAvailable);
        }
        return isAvailable;
      }),
      catchError(error => {
        if (_isDev()) {
          _log(`BedrockModelValidationError for ${modelId}:`, error);
        }
        // Si hay error, asumimos que el modelo no está disponible
        return [false];
      })
    );
  }

  /**
   * Verifica el estado del servicio AWS Bedrock
   */
  checkBedrockHealth(): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');

    const url = `${this.bedrockApiUrl}/health`;

    return this.http.get(url, { headers }).pipe(
      map((health: any) => {
        if (_isDev()) {
          _log('BedrockHealth:', health);
        }
        return health;
      }),
      catchError(error => {
        if (_isDev()) {
          _log('BedrockHealthError:', error);
        }
        throw {
          error: error.message || 'Error al verificar el estado de AWS Bedrock',
          status: 'unhealthy',
        };
      })
    );
  }

  /**
   * Método de conveniencia que convierte AIRequest a BedrockRequest y usa Bedrock
   */
  generateWithBedrockFromAIRequest(request: AIRequest): Observable<AIResponse> {
    const bedrockRequest: BedrockRequest = {
      modelId: request.model || 'anthropic.claude-sonnet-4-6',
      prompt: request.prompt,
      configuration: {
        temperature: request.temperature || 0.7,
        maxTokens: request.maxTokens || 1000,
      },
      useStreaming: request.stream || false,
    };

    const generateMethod = request.stream ? this.generateWithBedrockStream(bedrockRequest) : this.generateWithBedrock(bedrockRequest);

    return generateMethod.pipe(
      map((response: BedrockResponse) => ({
        content: response.content || '',
        error: response.success ? undefined : response.errorMessage,
      })),
      catchError((error: BedrockResponse) => {
        throw {
          content: '',
          error: error.errorMessage || 'Error al comunicarse con AWS Bedrock',
        };
      })
    );
    /**
     * Verifica si el servicio de IA está disponible
     */
  }

  isServiceAvailable(): boolean {
    // return _isDev() && window.location.port === '4200';
    return true; // Siempre disponible para evitar problemas en producción
  }
}
